import { useState, useRef, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import debounce from "lodash/debounce"
import { Select } from "components-design-system"
import { Dropdown, Spin } from "antd"
import { LoadingOutlined } from "@ant-design/icons"
import OptionBadge from "./OptionBadge/OptionBadge"

// apis
import { getGlobalSearch } from "api/hooks/global"

// context
import useAutoClose from "utilities/useAutoClose"

// styles
import "./useGlobalSearch.scss"

const useGlobalSearch = () => {
  const navigate = useNavigate()
  // search for global ID
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)

  // searchValue ensures dropdown is closed when no search string but options are available:
  const [searchValue, setSearchValue] = useState("")
  // dropdownVisible ensures dropdown is closed when user clicks outside component:
  const [dropdownVisible, setDropdownVisible] = useState(false)

  const dropdownRef = useRef()
  useAutoClose(dropdownRef, setDropdownVisible)

  const handleStudySearch = async (value) => {
    let val = value // changed name as it was shadowing state variable
    setSearchValue(value)
    if (!value) val = null
    return await debouncedSearch.current(val)
  }

  // find all studies for global ID search
  const onSearch = async (value) => {
    if (typeof value === "string") {
      try {
        setDropdownVisible(true)
        setLoading(true)
        const ret = await getGlobalSearch(value)
        const options = ret?.data?.results
        if (options) {
          if (options.length > 0) {
            setOptions(options)
          } else {
            setOptions([
              {
                id: "no-results",
              },
            ])
          }
        }
        return ret
      } catch (error) {
        console.error("Error fetching global search results:", error)
        setOptions([
          {
            id: "no-results",
          },
        ])
      } finally {
        setLoading(false)
      }
    }
  }

  const onClick = async (option) => {
    setSearchValue("")
    setDropdownVisible(false)
    setOptions([])
    await new Promise((r) => setTimeout(r, 300))
    if (option.result_type === "study") {
      navigate(`/study/${option.id}/overview`)
    } else if (option.result_type === "dsrequest") {
      navigate(`/request/${option.id}/`)
    } else if (option.result_type === "document") {
      navigate(`/file/${option.id}/`)
    }
  }

  /**
   * Highlights occurrences of a specified text within a given string.
   *
   * @param {string} text - The text in which to search for the highlight.
   * @param {string} highlight - The text to highlight within the given text.
   * @returns {string|JSX.Element[]} - The text with highlighted parts wrapped in a <span> element with a yellow background, or the original text if no highlight is specified.
   */
  const highlightText = (text, highlight) => {
    try {
      if (!highlight || !text) return text
      const regex = new RegExp(`(${highlight})`, "gi")
      const parts = text.split(regex)
      return parts.map((part, index) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <span
            key={index}
            style={{ backgroundColor: "yellow" }}
          >
            {part}
          </span>
        ) : (
          part
        )
      )
    } catch (error) {
      console.error({ error })
    }
  }

  useEffect(() => {
    if (!searchValue || searchValue.length === 0) {
      setDropdownVisible(false)
    } else if (Array.isArray(options) && options.length > 0) {
      setDropdownVisible(true)
    }
  }, [options, searchValue])

  const debouncedSearch = useRef(debounce(onSearch, 400))

  const classNames = ["global-search-dropdown"]

  /**
   * Converts a snake_case string to a camel case string with each word capitalized.
   * @param {string} key - The snake_case string to format.
   * @returns {string} - The formatted camel case string.
   */
  const formatKey = (key) =>
    key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")

  const GlobalSearch = (
    <div
      className="global-search-wrap"
      id="global-search-wrap-id"
      ref={dropdownRef}
    >
      <Dropdown
        getPopupContainer={() => document.getElementById("global-search-wrap-id")}
        menu={{
          items: loading
            ? [
                {
                  key: "loading",
                  label: (
                    <div style={{ textAlign: "center" }}>
                      <Spin indicator={<LoadingOutlined spin />} />
                    </div>
                  ),
                },
              ]
            : options.map((option, i) => {
                if (option.id === "no-results") {
                  return {
                    key: option.id,
                    label: "No matching records found",
                    style: { cursor: "auto" },
                  }
                } else {
                  return {
                    key: i.toString(),
                    label: (
                      <>
                        <span
                          className="global-search-dropdown-option"
                          onClick={() => onClick(option)}
                        >
                          <OptionBadge type={option.result_type} />
                          <div className="search_result">{highlightText(option.search_result, searchValue)}</div>
                        </span>
                        <ul>
                          {option.res_data &&
                            Object.entries(option.res_data).map(([key, value]) => {
                              // Check if the value includes the searchValue
                              if (value && value.toLowerCase().includes(searchValue.toLowerCase())) {
                                return (
                                  <li key={key}>
                                    <span>{formatKey(key)}:</span> {highlightText(value, searchValue)}
                                  </li>
                                )
                              }
                              return null
                            })}
                        </ul>
                      </>
                    ),
                  }
                }
              }),
        }}
        overlayClassName={classNames.join(" ")}
        open={dropdownVisible}
      >
        <>
          <Select
            options={options}
            placeholder="Search studies, requests, or files"
            size="large"
            onSearch={(value) => handleStudySearch(value)}
            // value={null} // reset value when empty
            searchValue={searchValue}
            open={false}
            className="global-search-select-wrap" // wrapperClassName
            selectClassname="global-search-select"
            showSearch
            allowClear={false}
            suffixIcon={null}
          />
          <i
            type="button"
            className="fas fa-search"
            aria-label="toggle menu"
          />
        </>
      </Dropdown>
    </div>
  )

  return {
    GlobalSearch,
    options,
    searchValue,
    handleStudySearch,
    onSearch,
  }
}

export default useGlobalSearch
